import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

function ReassignPoliciesDialog({
  reassignDialogOpen,
  setReassignDialogOpen,
  reassignFromMember,
  replacementUsers,
  selectedReplacementUser,
  setSelectedReplacementUser,
  selectedBondOptions,
  setSelectedBondOptions,
  availableBonds,
  bondsLoading,
  bondsError,
  reassignSubmitting,
  handleSubmitReassign,
}) {
  return (
    <Dialog
      open={reassignDialogOpen}
      onClose={() => setReassignDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ backgroundColor: "primary.main", color: "white" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SwapHorizIcon />
          Re-assign Policies
        </Box>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Position: <strong>{reassignFromMember?.Position || "N/A"}</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            From:{" "}
            <strong>{reassignFromMember?.lawfirm_user_name || "N/A"}</strong>
          </Typography>
          <Autocomplete
            options={
              (replacementUsers || []).map((u) => ({
                label: u.lawfirm_user_name,
                value: u.lawfirm_user_id,
              })) }
            value={selectedReplacementUser}
            onChange={(e, v) => setSelectedReplacementUser(v)}
            getOptionLabel={(o) => o?.label || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={(params) => (
              <TextField {...params} label="Select replacement user" />
            )}
          />

          <Autocomplete
            multiple
            options={[
              ...(availableBonds?.length
                ? [{ label: "All Bonds", value: "ALL" }]
                : []),
              ...(availableBonds || []).map((bond) => ({
                label: `${bond.bond_id} - ${bond.bond_type} - ${
                  bond.name || "N/A"
                } (${bond.status})`,
                value: bond.bond_id,
              })),
            ]}
            value={selectedBondOptions}
            onChange={(e, v) => {
              const includesAll = v.some((opt) => opt.value === "ALL");
              if (includesAll) {
                setSelectedBondOptions([{ label: "All Bonds", value: "ALL" }]);
              } else {
                setSelectedBondOptions(v.filter((opt) => opt.value !== "ALL"));
              }
            }}
            getOptionLabel={(o) => o?.label || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={(params) => (
              <TextField {...params} label="Select bond numbers" />
            )}
          />
          {bondsLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Loading bonds for this user...
              </Typography>
            </Box>
          )}
          {bondsError && !bondsLoading && (
            <Typography variant="body2" sx={{ color: "error.main" }}>
              {bondsError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={() => setReassignDialogOpen(false)} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmitReassign}
          color="primary"
          variant="contained"
          disabled={
            !selectedReplacementUser ||
            selectedBondOptions.length === 0 ||
            reassignSubmitting ||
            bondsLoading
          }
        >
          {reassignSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Submit"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReassignPoliciesDialog;
